/** @jsx isolateComponent */

import type { MouseEvent as ReactMouseEvent } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { Colors, IconButton } from '@zapier/design-system';

type Props = {
  /*
   * `aria-label` for the `IconButton` if `label` isn't supplied.
   */
  ariaLabel?: string;
  /*
   * `aria-hidden` for the `IconButton`.
   */
  ariaHidden?: boolean;
  /*
   * Indicates whether or not `IconButton` is disabled.
   */
  disabled?: boolean;
  /**
   * `id` of the `input` that this component points to.
   */
  id?: string;
  /**
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * Label for the `IconButton`.
   */
  label?: string;
  /*
   * Callback to be executed when the button is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
};

const SearchIconButtonTheme = {
  custom: {
    base: {
      backgroundColor: 'transparent',
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: 'transparent',
      color: Colors.grey100,
    },
    hover: {
      backgroundColor: Colors.grey100,
      color: Colors.grey700,
    },
    focus: {
      backgroundColor: Colors.grey100,
      color: Colors.grey700,
      outlineColor: Colors.transparent,
    },
  },
};

const SearchToggler = (props: Props) => {
  return (
    <IconButton
      /*
      The search form is friendlier for screenreader users
      without these buttons.
      */
      ariaHidden={props.ariaHidden}
      ariaLabel={props.ariaLabel}
      color="custom"
      colorTheme={SearchIconButtonTheme}
      disabled={props.disabled}
      htmlFor={props.id}
      icon={props.isSearching ? 'formX' : 'magnifyingGlass'}
      iconSize={24}
      isPill={true}
      label={props.label}
      onClick={props.onClick}
      shouldCorrectTooltipPosition={false}
      tooltipPosition="south"
    />
  );
};

export default SearchToggler;
